import React, { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import clientService from '../services/ClientService';
import routerService from '../services/RouterService';

const ClientsPage = () => {
    const navigate = useNavigate();
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    const [routers, setRouters] = useState([]);
    const [openActionsFor, setOpenActionsFor] = useState(null);

    const [globalSearch, setGlobalSearch] = useState('');

    const [pageSize, setPageSize] = useState(10);
    const [page, setPage] = useState(1);

    const [filters, setFilters] = useState({
        id: '',
        nombre: '',
        cedula: '',
        rif: '',
        status: '',
        router: '',
        movil: '',
        correo: '',
    });

    const fetchClients = async () => {
        setLoading(true);
        setError('');
        try {
            const all = await clientService.getAllClients({ chunkSize: 1000 });
            setClients(all);
        } catch (err) {
            setError('No se pudo cargar la lista de clientes.');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchClients();
    }, []);

    useEffect(() => {
        const fetchRouters = async () => {
            try {
                const response = await routerService.getRouters();
                setRouters(response.data || []);
            } catch (err) {
                // Si falla, solo mostramos router_id como fallback
                setRouters([]);
            }
        };
        fetchRouters();
    }, []);

    const routersById = useMemo(() => {
        const map = new Map();
        routers.forEach((r) => map.set(r.id, r));
        return map;
    }, [routers]);

    const filteredClients = useMemo(() => {
        const norm = (v) => String(v ?? '').toLowerCase();
        const q = globalSearch.trim().toLowerCase();
        const f = {
            id: filters.id.trim().toLowerCase(),
            nombre: filters.nombre.trim().toLowerCase(),
            cedula: filters.cedula.trim().toLowerCase(),
            rif: filters.rif.trim().toLowerCase(),
            status: filters.status.trim().toLowerCase(),
            router: filters.router.trim().toLowerCase(),
            movil: filters.movil.trim().toLowerCase(),
            correo: filters.correo.trim().toLowerCase(),
        };

        return clients.filter((c) => {
            if (q) {
                const routerName = routersById.get(c.router_id)?.nombre_router;
                const haystack = [
                    c.id,
                    c.nombre,
                    c.cedula,
                    c.rif,
                    c.status,
                    routerName,
                    c.router_id,
                    c.movil,
                    c.correo,
                ]
                    .map((x) => norm(x))
                    .join(' ');

                if (!haystack.includes(q)) return false;
            }

            if (f.id && !norm(c.id).includes(f.id)) return false;
            if (f.nombre && !norm(c.nombre).includes(f.nombre)) return false;
            if (f.cedula && !norm(c.cedula).includes(f.cedula)) return false;
            if (f.rif && !norm(c.rif).includes(f.rif)) return false;
            if (f.status && !norm(c.status).includes(f.status)) return false;
            if (f.router) {
                const routerName = routersById.get(c.router_id)?.nombre_router;
                const routerHaystack = `${norm(routerName)} ${norm(c.router_id)}`;
                if (!routerHaystack.includes(f.router)) return false;
            }
            if (f.movil && !norm(c.movil).includes(f.movil)) return false;
            if (f.correo && !norm(c.correo).includes(f.correo)) return false;
            return true;
        });
    }, [clients, filters, globalSearch, routersById]);

    useEffect(() => {
        setPage(1);
    }, [filters, pageSize]);

    const { pagedClients, totalItems, totalPages, currentPage, fromItem, toItem } = useMemo(() => {
        const total = filteredClients.length;
        const size = pageSize === 'all' ? total : Number(pageSize);
        const pages = pageSize === 'all' ? 1 : Math.max(1, Math.ceil(total / Math.max(1, size)));
        const curr = Math.min(Math.max(1, page), pages);
        const start = pageSize === 'all' ? 0 : (curr - 1) * size;
        const end = pageSize === 'all' ? total : Math.min(total, start + size);
        const slice = filteredClients.slice(start, end);
        return {
            pagedClients: slice,
            totalItems: total,
            totalPages: pages,
            currentPage: curr,
            fromItem: total === 0 ? 0 : start + 1,
            toItem: end,
        };
    }, [filteredClients, page, pageSize]);

    const pageItems = useMemo(() => {
        if (totalPages <= 7) {
            return Array.from({ length: totalPages }, (_, i) => i + 1);
        }

        const items = new Set([1, totalPages, currentPage]);
        if (currentPage - 1 > 1) items.add(currentPage - 1);
        if (currentPage + 1 < totalPages) items.add(currentPage + 1);

        const sorted = Array.from(items).sort((a, b) => a - b);
        const out = [];
        for (let i = 0; i < sorted.length; i++) {
            const n = sorted[i];
            out.push(n);
            const next = sorted[i + 1];
            if (next && next - n > 1) out.push('...');
        }
        return out;
    }, [currentPage, totalPages]);

    const handleFilterChange = (e) => {
        const { name, value } = e.target;
        setFilters((prev) => ({ ...prev, [name]: value }));
    };

    const handleDeleteClick = async (clientId) => {
        if (window.confirm('¿Estás seguro de que quieres eliminar este cliente?')) {
            try {
                await clientService.deleteClient(clientId);
                fetchClients();
            } catch (err) {
                setError('Error al eliminar el cliente.');
            }
        }
    };

    return (
        <div className="container mx-auto p-4" onClick={() => setOpenActionsFor(null)}>
            <div className="flex items-center justify-between mb-6">
                <h1 className="text-3xl font-bold text-gray-800">Clientes</h1>
                <div className="flex items-center gap-2">
                    <button
                        onClick={() => navigate('/clients/import')}
                        className="border px-4 py-2 rounded hover:bg-gray-50"
                    >
                        Importar CSV
                    </button>
                    <button
                        onClick={() => navigate('/clients/new')}
                        className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
                    >
                        Nuevo Cliente
                    </button>
                </div>
            </div>

            <div className="bg-white p-6 rounded-lg shadow-md">
                {loading && <p>Cargando...</p>}
                {error && <p className="text-red-500">{error}</p>}

                <div className="flex items-center justify-between mb-4">
                    <div className="flex items-center gap-2">
                        <span className="text-sm text-gray-700">Mostrar</span>
                        <select
                            value={pageSize}
                            onChange={(e) => {
                                const v = e.target.value;
                                setPageSize(v === 'all' ? 'all' : Number(v));
                            }}
                            className="border rounded px-2 py-1 text-sm"
                        >
                            <option value={5}>5</option>
                            <option value={10}>10</option>
                            <option value={25}>25</option>
                            <option value={100}>100</option>
                            <option value="all">Todos</option>
                        </select>
                        <span className="text-sm text-gray-700">registros</span>
                    </div>

                    <div className="flex items-center gap-4">
                        <div className="text-sm text-gray-600">
                            Mostrando {fromItem} a {toItem} de {totalItems}
                        </div>
                        <input
                            type="text"
                            value={globalSearch}
                            onChange={(e) => setGlobalSearch(e.target.value)}
                            placeholder="Buscar..."
                            className="border rounded px-3 py-1 text-sm"
                            onClick={(e) => e.stopPropagation()}
                        />
                    </div>
                </div>

                <div className="overflow-x-auto">
                    <table className="min-w-full bg-white">
                        <thead className="bg-gray-100">
                            <tr>
                                <th className="py-2 px-4 text-left border-r border-gray-200">ID</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">Nombre</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">Cédula</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">RIF</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">Status</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">Router</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">Móvil</th>
                                <th className="py-2 px-4 text-left border-r border-gray-200">Correo</th>
                                <th className="py-2 px-4 text-left">Acciones</th>
                            </tr>
                            <tr>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="id"
                                        value={filters.id}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="nombre"
                                        value={filters.nombre}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="cedula"
                                        value={filters.cedula}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="rif"
                                        value={filters.rif}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="status"
                                        value={filters.status}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="router"
                                        value={filters.router}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="movil"
                                        value={filters.movil}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4 border-r border-gray-200">
                                    <input
                                        name="correo"
                                        value={filters.correo}
                                        onChange={handleFilterChange}
                                        placeholder="Filtrar..."
                                        className="w-full px-2 py-1 border rounded"
                                    />
                                </th>
                                <th className="py-2 px-4" />
                            </tr>
                        </thead>
                        <tbody>
                            {!loading && filteredClients.length === 0 && (
                                <tr>
                                    <td colSpan="9" className="text-center py-6 text-gray-500">No hay clientes</td>
                                </tr>
                            )}
                            {pagedClients.map((c) => {
                                const routerName = routersById.get(c.router_id)?.nombre_router;
                                return (
                                    <tr key={c.id} className="border-b hover:bg-gray-50">
                                        <td className="py-2 px-4 border-r border-gray-100">{c.id}</td>
                                        <td className="py-2 px-4 border-r border-gray-100">
                                            <Link
                                                to={`/clients/${c.id}`}
                                                className="text-blue-600 hover:underline"
                                                onClick={() => setOpenActionsFor(null)}
                                            >
                                                {c.nombre}
                                            </Link>
                                        </td>
                                        <td className="py-2 px-4 border-r border-gray-100">{c.cedula || ''}</td>
                                        <td className="py-2 px-4 border-r border-gray-100">{c.rif || ''}</td>
                                        <td className="py-2 px-4 border-r border-gray-100">{c.status || ''}</td>
                                        <td className="py-2 px-4 border-r border-gray-100">{routerName || c.router_id || ''}</td>
                                        <td className="py-2 px-4 border-r border-gray-100">{c.movil || ''}</td>
                                        <td className="py-2 px-4 border-r border-gray-100">{c.correo || ''}</td>
                                        <td className="py-2 px-4 relative">
                                            <button
                                                type="button"
                                                title="Acciones"
                                                className="p-2 rounded hover:bg-gray-100"
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenActionsFor((prev) => (prev === c.id ? null : c.id));
                                                }}
                                            >
                                                <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2">
                                                    <path d="M21 2l-2 2m-1 1l-2 2m5-5l1 1a2 2 0 010 2.828l-3.172 3.172a2 2 0 01-2.828 0l-1-1" />
                                                    <path d="M3 21l6-2 10-10-4-4L5 15 3 21z" />
                                                </svg>
                                            </button>

                                            {openActionsFor === c.id && (
                                                <div
                                                    className="absolute right-0 mt-2 bg-white border rounded shadow z-10 flex"
                                                    onClick={(e) => e.stopPropagation()}
                                                >
                                                    <button
                                                        type="button"
                                                        title="Editar"
                                                        className="p-2 hover:bg-gray-100"
                                                        onClick={() => navigate(`/clients/${c.id}`)}
                                                    >
                                                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-gray-700" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M12 20h9" />
                                                            <path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4 12.5-12.5z" />
                                                        </svg>
                                                    </button>
                                                    <button
                                                        type="button"
                                                        title="Eliminar"
                                                        className="p-2 hover:bg-gray-100"
                                                        onClick={() => handleDeleteClick(c.id)}
                                                    >
                                                        <svg viewBox="0 0 24 24" className="w-5 h-5 text-red-600" fill="none" stroke="currentColor" strokeWidth="2">
                                                            <path d="M3 6h18" />
                                                            <path d="M8 6V4h8v2" />
                                                            <path d="M19 6l-1 14H6L5 6" />
                                                            <path d="M10 11v6" />
                                                            <path d="M14 11v6" />
                                                        </svg>
                                                    </button>
                                                </div>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-end mt-4 gap-2">
                        <button
                            type="button"
                            className="px-3 py-1 border rounded disabled:opacity-50"
                            onClick={() => setPage((p) => Math.max(1, p - 1))}
                            disabled={currentPage === 1}
                        >
                            &lt;
                        </button>

                        {pageItems.map((it, idx) => (
                            it === '...' ? (
                                <span key={`dots-${idx}`} className="px-2 text-gray-500">...</span>
                            ) : (
                                <button
                                    key={it}
                                    type="button"
                                    className={`px-3 py-1 border rounded ${it === currentPage ? 'bg-blue-500 text-white border-blue-500' : ''}`}
                                    onClick={() => setPage(it)}
                                >
                                    {it}
                                </button>
                            )
                        ))}

                        <button
                            type="button"
                            className="px-3 py-1 border rounded disabled:opacity-50"
                            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                            disabled={currentPage === totalPages}
                        >
                            &gt;
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

export default ClientsPage;
